import { ThemeEvents } from '@theme/events';

class BmhProductSticky extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.section = this.closest('.shopify-section');
    this.bar = this.querySelector('.bmh-product-sticky');
    const config = this.querySelector('[data-sticky-config]');
    if (!this.section || !this.bar || !config) return;
    this.config = JSON.parse(config.textContent);
    this.button = this.bar.querySelector('[data-sticky-submit]');
    this.label = this.bar.querySelector('[data-sticky-label]');
    this.price = this.bar.querySelector('[data-sticky-price]');
    this.quantity = this.bar.querySelector('[data-sticky-quantity]');
    this.controller = new AbortController();
    this.visible = false;
    this.pendingVariant = false;
    this.busy = false;
    this.frame = 0;
    document.body.append(this.bar);
    const options = { signal: this.controller.signal };
    const passive = { ...options, passive: true };
    window.addEventListener('scroll', this.schedule, passive);
    window.addEventListener('resize', this.schedule, passive);
    window.addEventListener('pageshow', this.schedule, options);
    window.visualViewport?.addEventListener('resize', this.schedule, passive);
    window.visualViewport?.addEventListener('scroll', this.schedule, passive);
    document.addEventListener('focusin', this.schedule, options);
    document.addEventListener('focusout', this.schedule, options);
    document.addEventListener('toggle', this.schedule, { ...options, capture: true });
    this.section.addEventListener('change', this.schedule, options);
    this.section.addEventListener('input', this.schedule, options);
    this.section.addEventListener(ThemeEvents.quantitySelectorUpdate, this.schedule, options);
    this.section.addEventListener(ThemeEvents.variantSelected, () => {
      this.pendingVariant = true;
      this.schedule();
    }, options);
    this.section.addEventListener(ThemeEvents.variantUpdate, (event) => {
      const productId = event.detail?.data?.productId;
      if (productId && String(productId) !== this.dataset.productId) return;
      const updatedSticky = event.detail?.data?.html?.querySelector('bmh-product-sticky');
      const updated = updatedSticky?.querySelector('[data-sticky-config]');
      if (updated) this.config = JSON.parse(updated.textContent);
      const updatedImage = updatedSticky?.querySelector('.bmh-product-sticky__image');
      const image = this.bar.querySelector('.bmh-product-sticky__image');
      if (updatedImage && image) {
        for (const attribute of ['src', 'srcset', 'sizes', 'width', 'height']) {
          const value = updatedImage.getAttribute(attribute);
          if (value) image.setAttribute(attribute, value);
        }
      }
      this.pendingVariant = false;
      this.busy = false;
      this.schedule();
    }, options);
    for (const eventName of [ThemeEvents.cartUpdate, ThemeEvents.cartError]) {
      document.addEventListener(eventName, () => {
        this.busy = false;
        clearTimeout(this.busyTimeout);
        this.schedule();
      }, options);
    }
    this.button.addEventListener('click', () => {
      this.findTarget();
      this.sync();
      if (!this.target || this.button.disabled || this.busy) return;
      this.busy = true;
      this.sync();
      this.target.click();
      this.busyTimeout = window.setTimeout(() => {
        this.busy = false;
        this.schedule();
      }, 15000);
    }, options);
    this.resizeObserver = new ResizeObserver(this.schedule);
    this.resizeObserver.observe(this.section);
    this.resizeObserver.observe(this.bar);
    this.mutationObserver = new MutationObserver(this.schedule);
    this.mutationObserver.observe(this.section, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'aria-busy', 'value', 'hidden', 'class']
    });
    this.dialogObserver = new MutationObserver(this.schedule);
    this.dialogObserver.observe(document.body, { attributes: true, subtree: true, attributeFilter: ['open'] });
    this.footer = document.querySelector('footer, [class*="footer-group"]');
    this.intersectionObserver = new IntersectionObserver(this.schedule);
    if (this.footer) this.intersectionObserver.observe(this.footer);
    this.schedule();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    this.dialogObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    cancelAnimationFrame(this.frame);
    clearTimeout(this.busyTimeout);
    this.setVisible(false);
    this.bar?.remove();
    this.controller = null;
  }

  schedule = () => {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.update();
    });
  };

  isRendered(element) {
    if (!(element instanceof HTMLElement)) return false;
    const rect = element.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) return false;
    return element.checkVisibility ? element.checkVisibility({ visibilityProperty: true }) : getComputedStyle(element).visibility !== 'hidden';
  }

  findTarget() {
    const selector = '[data-bmh26-cta], .ai-premium-purchase__add-to-cart, button[name="add"], button[ref="addToCartButton"], [data-testid="standalone-add-to-cart"]';
    const candidates = this.section.querySelectorAll(selector);
    const next = [...candidates].find(element => this.isRendered(element));
    if (next !== this.target) {
      if (this.target) this.intersectionObserver?.unobserve(this.target);
      this.target = next || null;
      if (this.target) this.intersectionObserver?.observe(this.target);
    }
    this.form = this.target?.closest('product-form-component')?.querySelector('form') || this.target?.closest('form');
  }

  sync() {
    const variantId = this.form?.querySelector('[name="id"]')?.value;
    const variant = this.config.variants[variantId];
    const unavailable = !variantId;
    const sourceDisabled = !this.target || this.target.disabled || this.target.getAttribute('aria-disabled') === 'true';
    const sourceBusy = this.target?.getAttribute('aria-busy') === 'true';
    const disabled = sourceDisabled || this.pendingVariant || this.busy || sourceBusy;
    if (this.button.disabled !== disabled) this.button.disabled = disabled;
    const busy = Boolean(this.busy || sourceBusy);
    if (this.button.getAttribute('aria-busy') !== String(busy)) this.button.setAttribute('aria-busy', String(busy));
    const label = unavailable ? this.config.unavailable : variant?.available === false ? this.config.soldOut : this.config.add;
    if (this.label.textContent !== label) this.label.textContent = label;
    if (variant && this.price.textContent !== variant.price) this.price.textContent = variant.price;
    const count = Number(this.form?.querySelector('[name="quantity"]')?.value || 1);
    const quantity = count > 1 ? `×${count}` : '';
    if (this.quantity.textContent !== quantity) this.quantity.textContent = quantity;
    this.quantity.hidden = !quantity;
  }

  update() {
    if (!this.isConnected || !this.bar) return;
    this.findTarget();
    this.sync();
    if (!this.target) {
      this.setVisible(false);
      return;
    }
    const rect = this.target.getBoundingClientRect();
    const passed = rect.bottom <= (this.visible ? 0 : -8);
    const footerRect = this.footer?.getBoundingClientRect();
    const atFooter = footerRect && footerRect.top < innerHeight && footerRect.bottom > 0;
    const overlay = document.querySelector('dialog[open], [role="dialog"][aria-modal="true"]:not([hidden])');
    const blocked = overlay && this.isRendered(overlay);
    const editing = document.activeElement?.matches('input:not([type="radio"]):not([type="checkbox"]), textarea, [contenteditable="true"]');
    const keyboard = editing && window.visualViewport && innerHeight - window.visualViewport.height > 140;
    this.setVisible(Boolean(passed && !atFooter && !blocked && !keyboard));
    if (this.visible) {
      const height = `${Math.ceil(this.bar.getBoundingClientRect().height)}px`;
      if (document.body.style.getPropertyValue('--bmh-product-sticky-height') !== height) document.body.style.setProperty('--bmh-product-sticky-height', height);
    }
  }

  setVisible(visible) {
    if (!this.bar || this.visible === visible) return;
    this.visible = visible;
    this.bar.dataset.visible = String(visible);
    this.bar.setAttribute('aria-hidden', String(!visible));
    this.bar.inert = !visible;
    document.body.classList.toggle('bmh-product-sticky-visible', visible);
  }
}

if (!customElements.get('bmh-product-sticky')) customElements.define('bmh-product-sticky', BmhProductSticky);
